import { Image } from '@chakra-ui/image';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import React from 'react';
import { useDispatch } from 'react-redux';

import Feedback from '../../../../../assets/icons/icon_feedback.svg';
import FeedbackArrow from '../../../../../assets/icons/icon_feedback_arrow.svg';
import LogoutIcon from '../../../../../assets/icons/icon_logout.svg';
import MyProfile from '../../../../../assets/icons/icon_my_profile.svg';
import assignReviewThumbnail from '../../../../../assets/images/assign_and_review_thumbnail.png';
import newCoursesThumbnail from '../../../../../assets/images/new_courses_thumbnail.png';
import { useSelectorFeedbackUrl } from '../../../../configuration/configuration.selectors';
import { sessionLogout } from '../../../../session';
import { useSessionStorage } from '../../../../storage/use-session-storage/use-session-storage';
import { openModal } from '../../modal-manager';
import { ModalVariants } from '../../modals';
import { Notifications } from '../../notifications/notifications';
import { NotificationHandlerProps } from '../../notifications/notifications.types';
import { UserMenuItemButton } from '../components/user-menu-item-button/user-menu-item-button';
import { VideoThumbnail } from '../components/video-thumbnail/video-thumbnail';
import { MenuPopperItem } from '../user-profile.styled';
import {
    GenerateMenuItemsProps,
    UserMenuGeneratorProps
} from '../user-profile.types';

const generateMenuItems = ({
    handleLogout,
    feedbackUrl,
    openUserProfileModal,
    notifications,
    hasNotifications,
    handleNotificationClick,
    openAssignReviewVideoModal,
    openNewCoursesVideoModal,
    isValidYearGroup,
    isValidCountryCode
}: GenerateMenuItemsProps) => {
    const userProfileMenuConfig = [
        {
            value: isValidYearGroup && isValidCountryCode ? <VideoThumbnail
                background={newCoursesThumbnail}
                title={t`userProfile.newCourses`}
                onClick={openNewCoursesVideoModal}
            /> : null,
            id: 'new-courses'
        },
        {
            value: <VideoThumbnail
                background={assignReviewThumbnail}
                title={t`userProfile.assignAndReview`}
                onClick={openAssignReviewVideoModal}
            />,
            id: 'assign-and-review'
        },
        {
            value: <UserMenuItemButton
                title={<Trans id={'userProfile.feedback'} />}
                leftIcon={<Image src={Feedback} />}
                rightIcon={<Image src={FeedbackArrow} />}
                onClick={() => window.open(feedbackUrl, '_blank')}
            />,
            id: 'feedback'
        },
        {
            value: hasNotifications ? <UserMenuItemButton
                leftIcon={<Notifications notifications={notifications} />}
                title={<Trans id={'userProfile.notifications'} />}
                onClick={handleNotificationClick}
            /> : null,
            id: 'notifications'
        },
        {
            value: <UserMenuItemButton
                leftIcon={<Image src={MyProfile} />}
                title={<Trans id={'userProfile.myprofile'} />}
                onClick={openUserProfileModal}
            />,
            id: 'my-profile'
        },
        {
            value: <UserMenuItemButton
                leftIcon={<Image src={LogoutIcon} />}
                title={<Trans id={'userProfile.logout'} />}
                onClick={handleLogout}
            />,
            id: 'logout'
        }
    ];

    return userProfileMenuConfig.map(({
        value, id
    }) => {
        return (
            <MenuPopperItem key={id}>
                {value}
            </MenuPopperItem>
        );
    });
};

export const useUserMenuGenerator = ({
    notifications,
    hasNotifications,
    classroomsYearGroups,
    validYearGroups,
    isValidCountryCode
}: UserMenuGeneratorProps) => {
    const dispatch = useDispatch();
    const feedbackUrl = useSelectorFeedbackUrl();
    const { clearSessionStorage } = useSessionStorage();
    const handleLogout = () => {
        dispatch(sessionLogout());
        clearSessionStorage();
    };
    const openUserProfileModal = () => {
        dispatch(openModal({ variant: ModalVariants.userProfile }));
    };
    const openAssignReviewVideoModal = () => {
        dispatch(openModal({ variant: ModalVariants.assignReviewVideoModal }));
    };
    const openNewCoursesVideoModal = () => {
        dispatch(openModal({ variant: ModalVariants.newCoursesVideoModal }));
    };
    const handleNotificationOpen = ({
        title, summary
    }: NotificationHandlerProps) => {
        dispatch(openModal({
            variant: ModalVariants.notification,
            modalProps: {
                title,
                summary
            }
        }));
    };
    const handleNotificationClick = () => {
        if (hasNotifications) {
            handleNotificationOpen(notifications[0]);
        }
    };
    const isValidYearGroup = validYearGroups.some((year) => classroomsYearGroups?.includes(year));

    return generateMenuItems({
        feedbackUrl,
        handleLogout,
        openUserProfileModal,
        notifications,
        hasNotifications,
        handleNotificationClick,
        openAssignReviewVideoModal,
        openNewCoursesVideoModal,
        isValidYearGroup,
        isValidCountryCode
    });
};
